import {
  makeStyles,
  Tab as MUITab,
  Tabs as MUITabs,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Tabs, useTabsItems } from "@resource/atlas";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useEffect } from "react";
import { ResourceCustomTheme } from "styles/config";

const useStyles = makeStyles<ResourceCustomTheme>(
  (_theme: ResourceCustomTheme) => ({
    content: {
      marginTop: "2rem",
    },
  })
);

type TabRoute = {
  label: string;
  path: string;
  query?: string;
  isDisabled?: boolean;
};

type TabRouteProps = {
  routes: TabRoute[];
  useAtlas?: boolean;
  children?: ReactNode | ReactNode[];
};

function TabRouter({ routes, children, useAtlas = false }: TabRouteProps) {
  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme<ResourceCustomTheme>();
  const variant = useMediaQuery(theme.breakpoints.up("md"))
    ? "standard"
    : "fullWidth";

  // HACK: There's a bug with how material calculates tab underlines, we
  // just have to force this update.
  //
  // See: https://github.com/mui-org/material-ui/issues/9337#issuecomment-413789329
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("resize"));
  }, []);

  const url = new URL(router.asPath, process.env.NEXT_PUBLIC_NEXTJS_APP_ROOT);

  const tabs = useTabsItems(
    (i) =>
      routes.map(({ label, path, query, isDisabled }) =>
        i.tab({
          id: path,
          children: label,
          disabled: isDisabled,
          renderWrapper: (props) => (
            <Link href={query ? `${path}${query}` : path}>
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <a {...props} />
            </Link>
          ),
        })
      ),
    [routes]
  );

  const renderedTabs = useAtlas ? (
    <Tabs.Root selectedId={url.pathname}>
      <Tabs.List items={tabs} />
    </Tabs.Root>
  ) : (
    <MUITabs
      value={url.pathname}
      variant={variant}
      onChange={(_event, destinationUrl) => router.push(destinationUrl)}
    >
      {routes.map(({ label, path, isDisabled }) => (
        <MUITab key={path} value={path} label={label} disabled={isDisabled} />
      ))}
    </MUITabs>
  );
  return (
    <>
      {renderedTabs}
      <div className={classes.content}>{children}</div>
    </>
  );
}

export default TabRouter;
